import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from 'react'
import PropTypes from 'prop-types'
import { withTheme } from 'styled-components'
import RefContext from '../contexts/RefContext'

import { WithResources } from './withResources'
import { replaceLatexesWithMathHtml } from '../utils/mathUtils'
import AppConfig from '../../../../src/app-config'
import { sanitizeHtml } from '../utils/html'
import { configureJqueryHtmlPrefilter } from '../helpers'
import {
  hasYoutubeEmbed,
  stashYoutubeIframes,
  tokenizeYoutubeIframes,
} from '../utils/youtubeEmbedIframe'

export const withMathFormula = (WrappedComponent) => {
  const MathFormulaWrapped = (props) => {
    /**
     * this whole component needs rethinking.
     */
    const contextConfig = useContext(RefContext)
    const {
      dangerouslySetInnerHTML,
      isCollapse = false,
      style = {},
      fontSize,
      theme = {},
      className,
      color,
      onFinish,
    } = props
    const [loaded, setLoaded] = useState(false)
    const [newInnerHtml, setNewInnerHtml] = useState('')
    const contentRef = useRef(null)
    let elemClassName = className
    if (theme.isV1Migrated) {
      elemClassName += ' migrated-question'
    }
    useEffect(() => {
      if (!loaded) {
        setNewInnerHtml(dangerouslySetInnerHTML.__html)
        if (typeof onFinish === 'function') {
          onFinish(true)
        }
        return
      }
      setNewInnerHtml(
        replaceLatexesWithMathHtml(dangerouslySetInnerHTML.__html)
      )
      if (isCollapse && !!newInnerHtml && newInnerHtml.includes('iframe')) {
        setNewInnerHtml(
          newInnerHtml.replace('<iframe', '<iframe style="display:none" ')
        )
      }
      if (typeof onFinish === 'function') {
        onFinish(true)
      }
    }, [dangerouslySetInnerHTML?.__html, loaded])

    const sanitizedHtml = sanitizeHtml(newInnerHtml)
    const wrapperStyle = {
      ...style,
      color: color || theme.questionTextColor,
      fontSize: fontSize || theme.fontSize,
    }

    // Only content that actually embeds a YouTube video is touched; all other
    // content renders through the exact same path as before, byte-for-byte.
    // Collapsed content already hides its iframes above, so it stays untouched.
    const useTokenizedVideo = !isCollapse && hasYoutubeEmbed(sanitizedHtml)
    // Blank the raw youtube src before render so no tokenless embed loads (which
    // would flash error 152); the encrypted token is applied post-mount below.
    const finalHtml = useTokenizedVideo
      ? stashYoutubeIframes(sanitizedHtml)
      : sanitizedHtml

    // Keep the forwarded ref working while also getting a handle on the rendered
    // node so we can tokenize any YouTube iframes it contains.
    const setContentRef = useCallback(
      (node) => {
        contentRef.current = node
        const forwardedRef = contextConfig?.forwardedRef
        if (typeof forwardedRef === 'function') {
          forwardedRef(node)
        } else if (forwardedRef) {
          forwardedRef.current = node
        }
      },
      [contextConfig]
    )

    useEffect(() => {
      if (useTokenizedVideo && contentRef.current) {
        tokenizeYoutubeIframes(contentRef.current)
      }
    }, [finalHtml, useTokenizedVideo])

    return (
      <WithResources
        resources={[
          AppConfig.jqueryPath,
          `${AppConfig.katexPath}/katex.min.css`,
          `${AppConfig.katexPath}/katex.min.js`,
        ]}
        fallBack={<span />}
        onLoaded={() => {
          setLoaded(true)
          configureJqueryHtmlPrefilter()
        }}
      >
        <WrappedComponent
          {...props}
          ref={setContentRef}
          className={elemClassName}
          data-cy="styled-wrapped-component"
          dangerouslySetInnerHTML={{ __html: finalHtml }}
          style={wrapperStyle}
        />
      </WithResources>
    )
  }

  MathFormulaWrapped.propTypes = {
    dangerouslySetInnerHTML: PropTypes.object,
    className: PropTypes.string,
    isCollapse: PropTypes.bool.isRequired,
    fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      .isRequired,
    theme: PropTypes.object.isRequired,
    style: PropTypes.object,
  }

  MathFormulaWrapped.defaultProps = {
    dangerouslySetInnerHTML: {
      __html: '',
    },
    className: '',
    style: {},
  }

  return withTheme(MathFormulaWrapped)
}
